import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import Api from '../utils/Api';
import { useError } from './ErrorContext';
import { useLoading } from './LoadingContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { useRoutePath } from '../utils/getRoutePath';

const TimeOffRequestTypeContext = createContext();

export const useTimeOffRequestType = () => {
  const context = useContext(TimeOffRequestTypeContext);
  if (!context) {
    throw new Error('useTimeOffRequestType must be used within a TimeOffRequestTypeProvider');
  }
  return context;
};

export const TimeOffRequestTypeProvider = ({ children }) => {
   
  const location = useLocation();
  const navigate = useNavigate();
  const { hideLoading } = useLoading();
  const { user, getToken } = useAuth();
  const [api, setApi] = useState(null);
  const { handleApiError, formErrors, clearErrors } = useError();
  const [timeOffRequestTypes, setTimeOffRequestTypes] = useState([]);
  const [paginate, setPaginate] = useState(true);
  const getRoutePath = useRoutePath();
  const initialized = location.pathname === getRoutePath('/time-off-request-types');

  const getTimeOffRequestTypes = useCallback(async (page = 1, filters = {}) => {
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }

    try {
      const searchParams = new URLSearchParams(window.location.search);
      let query = '';
      if (filters?.q) {
        query = '&q=' + filters?.q;
      } else {
        const q = searchParams.get('q') || '';
        query = '&q=' + q;
      }
      if (filters?.active) {
        query += '&active=' + filters?.active;
      } else {
        const active = searchParams.get('active') || '';
        query += '&active=' + active;
      }

      let response = await apiInstance.call('time-off-request-types?page=' + page + '&paginate=' + paginate + '' + query, 'GET');
      if (filters?.all) {
        query = '';
        setPaginate(false);
        response = await apiInstance.call('time-off-request-types');
      }

      if (response.status === 200) {
        setPaginate(response.data.paginate);
        setTimeOffRequestTypes(response.data?.timeOffRequestTypes || []);
        hideLoading();
        if (filters) {
          return response.data.timeOffRequestTypes;
        }
      } else {
        hideLoading();
        handleApiError(response.error, {
          showAlert: false,
          redirectOn401: true,
          customMessage: 'Failed to fetch time off request types'
        });
      }
    } catch (error) {
      hideLoading();
    }
  }, [handleApiError, hideLoading, paginate, getToken]);
 

  useEffect(() => {
    const apiInstance = Api(() => getToken());
    setApi(apiInstance);
  }, [getToken]);

  useEffect(() => {
    if (initialized && api) {
      getTimeOffRequestTypes();
    }
  }, [initialized, api, getTimeOffRequestTypes]);
 
  const addTimeOffRequestType = async (type) => {
    if (!api) {
      return;
    }

    const formData = new FormData();
    Object.keys(type).forEach(key => {
      if (type[key] !== null) {
        formData.append(key, type[key]);
      }
    });
    formData.append('company_id', user?.current_company?.id);
    formData.append('user_id', user?.id);

    try {
      const response = await api.call('time-off-request-types', 'POST', formData, true, true);
      
      if (response.status === 200) {
        const timeOffRequestTypes = response.data.timeOffRequestTypes;
        setTimeOffRequestTypes(timeOffRequestTypes);
        return { status: response.status };
      } else {
        if (response.error.status === 422) {
          return {
            status: response.error.status,
            errors: response.error.validationErrors
          };
        } else {
          return handleApiError(response.error, {
            showAlert: false,
            redirectOn401: true,
            customMessage: 'Failed to add time off request type'
          });
        }
      }
    } catch (error) {
      return handleApiError(error, {
        showAlert: true,
        redirectOn401: true,
        customMessage: 'Failed to add time off request type'
      });
    }
  };

  const getTimeOffRequestTypeById = (id) => {
    let timeOffRequestTypeData = timeOffRequestTypes?.data?.find(type => {
      return Number(type.id) === Number(id);
    });
    return timeOffRequestTypeData;
  };

  const updateTimeOffRequestType = async (id, updatedType) => {
    if (!api) {
      return;
    }

    const formData = new FormData();
    Object.keys(updatedType).forEach(key => {
      if (updatedType[key] !== null) {
        formData.append(key, updatedType[key]);
      }
    });
    formData.append('company_id', user?.current_company?.id);
    formData.append('user_id', user?.id);

    try {
      const response = await api.call(`time-off-request-types/${id}`, 'POST', formData, true, true);
      if (response.status === 200) {
        const updatedTimeOffRequestTypes = timeOffRequestTypes?.data?.map(type =>
          Number(type.id) === Number(id) ? {
            ...response.data.time_off_request_type,
          } : type
        );
        setTimeOffRequestTypes(updatedTimeOffRequestTypes);
        return { status: response.status };
      } else {
        if (response.error.status === 422) {
          return {
            status: response.error.status,
            errors: response.error.validationErrors
          };
        } else {
          return handleApiError(response.error, {
            showAlert: false,
            redirectOn401: true,
            customMessage: 'Failed to update time off request type'
          });
        }
      }
    } catch (error) {
      return handleApiError(error, {
        showAlert: false,
        redirectOn401: true,
        customMessage: 'Failed to update time off request type'
      });
    }
  };

  const deleteTimeOffRequestType = async (id) => {
    if (!api) {
      return;
    }

    try {
      const response = await api.call(`time-off-request-types/${id}`, 'DELETE', null, true);
      if (response.status === 200) {
        navigate(getRoutePath('/time-off-request-types'));
        return { status: response.status };
      } else {
        return handleApiError(response.error, {
          showAlert: false,
          redirectOn401: true,
          customMessage: 'Failed to delete time off request type'
        });
      }
    } catch (error) {
      return handleApiError(error, {
        showAlert: true,
        redirectOn401: true,
        customMessage: 'Failed to delete time off request type'
      });
    }
  };

  return (
    <TimeOffRequestTypeContext.Provider
      value={{
        timeOffRequestTypes,
        addTimeOffRequestType,
        getTimeOffRequestTypes: (page, filters) => getTimeOffRequestTypes(page, filters),
        getTimeOffRequestTypeById,
        updateTimeOffRequestType,
        deleteTimeOffRequestType,
        formErrors,
        paginate,
        clearErrors
      }}
    >
      {children}
    </TimeOffRequestTypeContext.Provider>
  );
}; 